import { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Badge,
  Modal,
  Popconfirm,
  message,
} from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMitigationTypeMappings,
  createMitigationTypeMapping,
  updateMitigationTypeMapping,
  deleteMitigationTypeMapping,
  getProjectTypes,
} from '../../api/project_type';

const { Title, Text } = Typography;

const MITIGATION_TYPE_OPTIONS = [
  { value: 'Avoidance', label: 'Avoidance' },
  { value: 'Removals', label: 'Removals' },
  { value: 'Removals + Avoidance', label: 'Removals + Avoidance' },
];

const QUERY_KEY = ['mitigationTypeMappings'];

type StatusType = 'mapped' | 'needs-mapping';

function MitigationTypeMapper() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createCalyxProjectType, setCreateCalyxProjectType] = useState<string | null>(null);
  const [createMitigationType, setCreateMitigationType] = useState<string | null>(null);

  const { data: rawData = [], isLoading, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const data = await getMitigationTypeMappings();
      const rows = data?.response ?? (Array.isArray(data) ? data : []);
      return rows as any[];
    },
  });

  const { data: projectTypesData } = useQuery({
    queryKey: ['projectTypes'],
    queryFn: getProjectTypes,
  });

  const projectTypeOptions = useMemo(() => {
    const raw = Array.isArray(projectTypesData) ? projectTypesData : projectTypesData?.response || [];
    return (raw as any[]).map((pt) => ({ value: pt.display_name, label: pt.display_name }));
  }, [projectTypesData]);

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({ id, calyx_project_type, mitigation_type }: { id: string; calyx_project_type: string; mitigation_type: string | null }) =>
      updateMitigationTypeMapping(id, { calyx_project_type, mitigation_type }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
        message.success('Mapping updated');
      },
      onError: () => { message.error('Failed to update mapping. Please try again.'); },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteMitigationTypeMapping(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
        message.success('Mapping deleted');
      },
      onError: () => { message.error('Failed to delete mapping. Please try again.'); },
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    () =>
      createMitigationTypeMapping({
        calyx_project_type: createCalyxProjectType!,
        ...(createMitigationType ? { mitigation_type: createMitigationType } : {}),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
        message.success('Mapping created');
        setCreateModalOpen(false);
        setCreateCalyxProjectType(null);
        setCreateMitigationType(null);
      },
      onError: (err: any) => { message.error(err?.message || 'Failed to create mapping. Please try again.'); },
    },
  );

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rawData as any[]).filter((row) => {
      const mapped = !!row.mitigation_type;
      if (statusFilter === 'mapped' && !mapped) return false;
      if (statusFilter === 'needs-mapping' && mapped) return false;
      if (!text) return true;
      const haystack = [
        row.project_type?.display_name || '',
        row.mitigation_type || '',
      ].join(' ').toLowerCase();
      return haystack.includes(text);
    });
  }, [rawData, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Calyx Project Type',
      key: 'project_type',
      sorter: (a: any, b: any) =>
        (a.project_type?.display_name || '').localeCompare(b.project_type?.display_name || ''),
      render: (_: any, record: any) => (
        <Text strong>{record.project_type?.display_name ?? '—'}</Text>
      ),
    },
    {
      title: 'Mitigation Type',
      key: 'mitigation_type',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.mitigation_type || '').localeCompare(b.mitigation_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select mitigation type"
          value={record.mitigation_type || undefined}
          options={MITIGATION_TYPE_OPTIONS}
          onChange={(val) =>
            updateMapping({
              id: record.id,
              calyx_project_type: record.project_type?.display_name,
              mitigation_type: val ?? null,
            })
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => {
        const sa = a.mitigation_type ? 'mapped' : 'needs-mapping';
        const sb = b.mitigation_type ? 'mapped' : 'needs-mapping';
        return sa.localeCompare(sb);
      },
      render: (_: any, record: any) =>
        record.mitigation_type ? (
          <Tag color="green">Mapped</Tag>
        ) : (
          <Tag color="red">Needs Mapping</Tag>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this mapping?"
          okText="Delete"
          okType="danger"
          onConfirm={() => deleteMapping(record.id)}
        >
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const needsMappingCount = (rawData as any[]).filter((r) => !r.mitigation_type).length;
  const loading = isLoading || isFetching || isUpdating || isDeleting;

  return (
    <>
    <Spin spinning={loading} tip="Loading…">
        <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search project type, mitigation type…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
            allowClear
          />
          <Space>
            <Text type="secondary">Show:</Text>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 200 }}
              options={[
                { value: 'all', label: 'All types' },
                { value: 'needs-mapping', label: 'Needs mapping' },
                { value: 'mapped', label: 'Mapped' },
              ]}
            />
            <Badge count={needsMappingCount} size="small" style={{ backgroundColor: '#fa541c' }} />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => queryClient.invalidateQueries(QUERY_KEY)}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add Mapping
            </Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filteredRows}
          rowKey="id"
          size="middle"
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        />
      </Spin>

      <Modal
        title="Add Mitigation Type Mapping"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateCalyxProjectType(null);
          setCreateMitigationType(null);
        }}
        onOk={() => createMapping()}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{ disabled: !createCalyxProjectType }}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Calyx Project Type</Text>
            <Text type="danger"> *</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Calyx Project Type"
              value={createCalyxProjectType || undefined}
              options={projectTypeOptions}
              onChange={(val) => setCreateCalyxProjectType(val ?? null)}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <Text strong>Mitigation Type</Text>
            <Text type="secondary"> (optional)</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select mitigation type"
              value={createMitigationType || undefined}
              options={MITIGATION_TYPE_OPTIONS}
              onChange={(val) => setCreateMitigationType(val ?? null)}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

export default MitigationTypeMapper;
